import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreditCard, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { AutoTopupFields } from "./AutoTopupFields";
import { saveWalletAutopay, startCardSetup, walletAutopay } from "../../api/wallet";

export const AutoTopup = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    walletAutopay().then(setForm);
  }, []);

  if (!form) return null;

  const set = (key, value) => setForm({ ...form, [key]: value });

  const save = async (patch = {}) => {
    setBusy(true);
    try {
      const next = { ...form, ...patch };
      const saved = await saveWalletAutopay({
        enabled: next.enabled,
        threshold_tokens: Number(next.threshold_tokens),
        topup_tokens: Number(next.topup_tokens),
      });
      setForm(saved);
      toast.success(t("wallet.autopaySaved"));
    } catch {
      toast.error(t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  const addCard = async () => {
    const { checkout_url } = await startCardSetup();
    window.location.href = checkout_url;
  };

  return (
    <NeuCard data-testid="autopay-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="label-xs flex items-center gap-2">
          <RefreshCcw className="h-4 w-4 text-primary" /> {t("wallet.autopayTitle")}
        </p>
        <NeuButton
          data-testid="autopay-toggle"
          className={`px-4 py-2 text-xs ${form.enabled ? "text-primary shadow-neu-in" : ""}`}
          disabled={busy}
          onClick={() => save({ enabled: !form.enabled })}
        >
          {form.enabled ? t("wallet.autopayOn") : t("wallet.autopayOff")}
        </NeuButton>
      </div>

      <p className="mt-2 text-xs text-muted-foreground">{t("wallet.autopayHint")}</p>

      <AutoTopupFields form={form} set={set} />

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <NeuButton variant="primary" data-testid="autopay-save" className="px-5 py-2 text-xs" disabled={busy} onClick={() => save()}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("wallet.autopaySave")}
        </NeuButton>
        <NeuButton data-testid="autopay-card-btn" className="flex items-center gap-2 px-4 py-2 text-xs" onClick={addCard}>
          <CreditCard className="h-4 w-4" />
          {form.card_saved ? `${form.card_brand} ••••${form.card_last4}` : t("wallet.autopayAddCard")}
        </NeuButton>
        {form.last_error ? (
          <span data-testid="autopay-error" className="text-xs font-bold text-destructive">
            {t("wallet.autopayFailed")}
          </span>
        ) : null}
      </div>
    </NeuCard>
  );
};
